import { FC, ReactNode, useState } from 'react';
import { DatePicker, Typography } from 'antd';
import moment, { Moment } from 'moment/moment';
import classnames from 'classnames';

import { Option } from 'models';
import { useTranslate } from 'hooks';
import Utils from 'utils/utils';
import { LOCALES } from 'utils/constants';
import { language } from 'utils';

import { CheckBoxGroup, FormItem, RadioGroup, SwitchButton, TextField } from 'componets/form';

import styles from './index.module.scss';

type FilterItemProps = {
	options?: Option[];
	name: string;
	title: string;
	isRange?: boolean;
	isField?: boolean;
	isDate?: boolean;
	isCheckbox?: boolean;
	isSwitch?: boolean;
	isRadio?: boolean;
	checked?: boolean;
	onClick?: (v: boolean) => void;
	isDropdown?: boolean;
	mark?: string;
	Dropdown?: () => ReactNode;
	minDate?: string;
	maxDate?: string;
};

export const FilterItem: FC<FilterItemProps> = ({
	options,
	name,
	title,
	isRange,
	isField,
	isSwitch,
	isCheckbox,
	checked = false,
	onClick,
	mark = '%',
	isRadio,
	isDate,
	isDropdown,
	Dropdown,
	minDate,
	maxDate,
}) => {
	const { fromText, toText } = useTranslate(['fromText', 'toText']);

	return (
		<div className={styles.filterItem}>
			{isSwitch && typeof onClick === 'function' ? (
				<FormItem name={name}>
					<SwitchButton label={title} loading={false} checked={checked} onClick={onClick} />
				</FormItem>
			) : (
				<>
					<Typography.Title level={5}>{title}</Typography.Title>

					{isField && (
						<FormItem
							name={name}
							className={styles.inputText}
							normalize={name === 'loanId' ? (v) => Utils.transformToFixedNumber(v) : undefined}
						>
							<TextField label="" />
						</FormItem>
					)}

					{isRange && (
						<div className="primaryMarket__row">
							<div className={styles.inputWrapper}>
								<FormItem
									name={`${name}[from]`}
									normalize={(v) => Utils.transformToFixedNumber(v)}
									className={styles.inputText}
								>
									<TextField label={fromText} />
								</FormItem>

								<span className={styles.percent}>{mark}</span>
							</div>

							<div className={styles.separator} />

							<div className={styles.inputWrapper}>
								<FormItem name={`${name}[to]`} normalize={(v) => Utils.transformToFixedNumber(v)}>
									<TextField label={toText} />
								</FormItem>

								<span className={styles.percent}>{mark}</span>
							</div>
						</div>
					)}

					{isCheckbox && options && (
						<FormItem name={name}>
							<CheckBoxGroup options={options} />
						</FormItem>
					)}

					{isRadio && options && (
						<FormItem name={name}>
							<RadioGroup options={options} />
						</FormItem>
					)}

					{isDate && (
						<div className={styles.row}>
							<FormItem name="minDateOfPurchase">
								<DateField label={fromText} maxDate={maxDate} />
							</FormItem>
							<FormItem name="maxDateOfPurchase">
								<DateField label={toText} minDate={minDate} />
							</FormItem>
						</div>
					)}
				</>
			)}

			{isDropdown && Dropdown && Dropdown()}
		</div>
	);
};

const DateField: FC<{
	onChange?: any;
	label: string;
	value?: Moment | null | undefined;
	minDate?: string;
	maxDate?: string;
}> = ({ onChange, label, value, minDate, maxDate }) => {
	const [focus, setFocus] = useState(false);

	return (
		<div>
			<DatePicker
				locale={(LOCALES as any)[language]}
				allowClear={false}
				inputReadOnly
				placeholder=""
				value={value ? moment(value) : undefined}
				suffixIcon={null}
				format="YYYY-MM-DD"
				onBlur={() => setFocus(false)}
				onFocus={() => setFocus(true)}
				onChange={(e) => {
					if (onChange) {
						onChange(moment(e).format('YYYY-MM-DD'));
					}
				}}
				disabledDate={(d) =>
					minDate
						? !d || d > moment() || d.isSameOrBefore(moment(minDate))
						: maxDate
						? !d || d.isAfter(moment(maxDate))
						: d > moment()
				}
			/>
			<span
				className={classnames('float-label', styles.label, {
					[styles.focused]: focus || value,
					'focused-float-label': focus,
				})}
			>
				{label}
			</span>
		</div>
	);
};
